import { Component, OnInit, OnDestroy } from '@angular/core';
import { TableService, OrderService, AuthService } from '@app/shared/services';
import { ConfiguracionService } from '../shared/services/configuracion.service';
import { Table, Zone, Location } from '@app/shared/models';
import { TableStatus } from '@app/shared/enums';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-floor-plan',
  templateUrl: './floor-plan.component.html',
  styleUrls: ['./floor-plan.component.scss']
})
export class FloorPlanComponent implements OnInit, OnDestroy {
  zones: Zone[] = [];
  /** Todas las zonas activas (para filtrar por sede). */
  allZones: Zone[] = [];
  tables: Table[] = [];
  locations: Location[] = [];
  selectedLocationId: string | null = null;
  selectedZoneId: string | null = null;
  selectedTables: Set<string> = new Set();
  showTableConfig = false;
  configMode: 'create' | 'edit' = 'create';
  selectedTable: Table | null = null;

  get canCreateMesas(): boolean {
    return this.hasPermiso('mesas_crear');
  }
  get canEditMesas(): boolean {
    return this.hasPermiso('mesas_editar');
  }
  /** Ver creación/edición según permisos (crear mesas/zonas o editar/eliminar). */
  get canEditFloorPlan(): boolean {
    return this.canCreateMesas || this.canEditMesas;
  }
  private hasPermiso(clave: string): boolean {
    const user = this.authService.getCurrentUser();
    return user ? this.configuracionService.hasPermiso(user.role, clave) : false;
  }

  /** Modal nueva zona */
  showZoneForm = false;
  zoneFormName = '';
  zoneFormType: 'SALA' | 'TERRAZA' | 'VIP' | 'BARRA' | 'PISO' = 'SALA';

  /** Modal confirmar eliminar zona */
  showDeleteZoneConfirm = false;
  zoneToDelete: Zone | null = null;
  deleteZoneMessage = '';
  deleteZoneHasOrders = false;

  private destroy$ = new Subject<void>();

  constructor(
    private tableService: TableService,
    private orderService: OrderService,
    private authService: AuthService,
    private configuracionService: ConfiguracionService
  ) { }

  ngOnInit(): void {
    this.loadLocations();
    this.loadZones();
    this.loadTables();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadZones(): void {
    this.tableService.zones$
      .pipe(takeUntil(this.destroy$))
      .subscribe(zones => {
        this.allZones = zones.filter(z => z.active !== false);
        this.zones = this.getZonesForDisplay();
        if (this.zones.length > 0 && (!this.selectedZoneId || !this.zones.find(z => z.id === this.selectedZoneId))) {
          this.selectedZoneId = this.zones[0].id;
        }
      });
  }

  private loadLocations(): void {
    this.tableService.locations$
      .pipe(takeUntil(this.destroy$))
      .subscribe(locations => {
        this.locations = locations.filter(l => l.active !== false);
      });
    this.tableService.selectedLocationId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(locationId => {
        this.selectedLocationId = locationId;
        this.zones = this.getZonesForDisplay();
        if (this.zones.length > 0 && (!this.selectedZoneId || !this.zones.find(z => z.id === this.selectedZoneId))) {
          this.selectedZoneId = this.zones[0].id;
        } else if (this.zones.length === 0) {
          this.selectedZoneId = null;
        }
        this.selectedTables.clear();
      });
  }

  /** Zonas visibles según la sede seleccionada. */
  getZonesForDisplay(): Zone[] {
    if (!this.selectedLocationId) return this.allZones;
    return this.allZones.filter(z => z.locationId === this.selectedLocationId || z.locationId == null);
  }

  /** La sede se cambia desde el header (solo admin); el floor-plan reacciona vía selectedLocationId$. */

  private loadTables(): void {
    this.tableService.tables$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tables => {
        this.tables = tables;
        // Forzar actualización cuando cambien las mesas
      });
  }

  onZoneSelected(zoneId: string): void {
    this.selectedZoneId = zoneId;
    this.selectedTables.clear();
  }

  getTablesByZone(): Table[] {
    if (!this.selectedZoneId) {
      return [];
    }
    return this.tables.filter(table => table.zoneId === this.selectedZoneId);
  }

  onTableClick(table: Table, event: MouseEvent): void {
    if (!this.canEditFloorPlan) {
      this.selectedTables.clear();
      this.selectedTables.add(table.id);
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.shiftKey) {
      if (this.selectedTables.has(table.id)) {
        this.selectedTables.delete(table.id);
      } else {
        this.selectedTables.add(table.id);
      }
    } else if ((event.ctrlKey || event.metaKey) && this.canEditMesas) {
      this.selectedTable = table;
      this.configMode = 'edit';
      this.showTableConfig = true;
    } else {
      this.selectedTables.clear();
      this.selectedTables.add(table.id);
    }
  }

  onTableDoubleClick(table: Table): void {
    if (!this.canEditMesas) return;
    this.selectedTable = table;
    this.configMode = 'edit';
    this.showTableConfig = true;
  }

  createNewTable(): void {
    this.selectedTable = null;
    this.configMode = 'create';
    this.showTableConfig = true;
  }

  onTableConfigSaved(table: Table): void {
    if (this.configMode === 'create') {
      this.tableService.createTable(table);
    } else {
      this.tableService.updateTable(table);
    }
    this.showTableConfig = false;
    this.selectedTable = null;
  }

  onTableConfigCancelled(): void {
    this.showTableConfig = false;
    this.selectedTable = null;
  }


  getTableStatusIcon(status: TableStatus): string {
    const icons: Record<TableStatus, string> = {
      [TableStatus.AVAILABLE]: '🟢',
      [TableStatus.OCCUPIED]: '🔵',
      [TableStatus.ORDER_IN_PROGRESS]: '🟡',
      [TableStatus.SERVING]: '🟠',
      [TableStatus.PENDING_PAYMENT]: '🔴',
    };
    return icons[status];
  }

  onTableMoved(event: { table: Table; x: number; y: number }): void {
    // Actualizar directamente las propiedades del objeto table (que es una instancia de Table)
    event.table.positionX = event.x;
    event.table.positionY = event.y;
    event.table.updatedAt = new Date();
    // El servicio actualizará la referencia en el array
    this.tableService.updateTable(event.table);
  }

  onTablesOrganized(organized: { table: Table; x: number; y: number }[]): void {
    // Actualizar todas las mesas organizadas
    organized.forEach(item => {
      item.table.positionX = item.x;
      item.table.positionY = item.y;
      item.table.updatedAt = new Date();
      this.tableService.updateTable(item.table);
    });
  }

  getSelectedZone(): Zone | undefined {
    if (!this.selectedZoneId) return undefined;
    return this.zones.find(z => z.id === this.selectedZoneId);
  }


  deleteTable(table: Table): void {
    this.tableService.deleteTable(table.id);
    if (this.selectedTables.has(table.id)) {
      this.selectedTables.delete(table.id);
    }
    if (this.selectedTable?.id === table.id) {
      this.selectedTable = null;
    }
  }

  openNewZoneForm(): void {
    this.zoneFormName = '';
    this.zoneFormType = 'SALA';
    this.showZoneForm = true;
  }

  closeZoneForm(): void {
    this.showZoneForm = false;
  }

  saveNewZone(): void {
    const name = this.zoneFormName?.trim();
    if (!name) return;
    const id = 'zone_' + Date.now();
    const zone = new Zone(id, name, this.zoneFormType);
    zone.locationId = this.selectedLocationId || this.locations[0]?.id || null;
    this.tableService.createZone(zone);
    this.selectedZoneId = id;
    this.showZoneForm = false;
  }

  /** Abre el modal de confirmación para eliminar/inactivar zona. */
  confirmDeleteZone(zone: Zone): void {
    const tablesInZone = this.tables.filter(t => t.zoneId === zone.id);
    const tableCount = tablesInZone.length;
    const hasOrders = tablesInZone.some(t => this.orderService.getOrdersByTableId(t.id).length > 0);
    this.zoneToDelete = zone;
    this.deleteZoneHasOrders = hasOrders;
    this.deleteZoneMessage = hasOrders
      ? `La zona "${zone.name}" tiene mesas con pedidos en el historial. Se inactivará la zona y sus ${tableCount} mesa(s) (no se borrarán los datos).`
      : `Se eliminará la zona "${zone.name}" y las ${tableCount} mesa(s) asociadas. Esta acción no se puede deshacer.`;
    this.showDeleteZoneConfirm = true;
  }

  closeDeleteZoneConfirm(): void {
    this.showDeleteZoneConfirm = false;
    this.zoneToDelete = null;
    this.deleteZoneMessage = '';
  }

  /** Ejecuta borrado o inactivación tras confirmar en el modal. */
  executeDeleteZone(): void {
    const zone = this.zoneToDelete;
    if (!zone) return;
    if (this.deleteZoneHasOrders) {
      this.tableService.inactivateZone(zone.id);
    } else {
      this.tableService.deleteZone(zone.id);
    }
    if (this.selectedZoneId === zone.id) {
      const remaining = this.zones.filter(z => z.id !== zone.id);
      this.selectedZoneId = remaining.length ? remaining[0].id : null;
      this.selectedTables.clear();
      this.selectedTable = null;
    }
    this.closeDeleteZoneConfirm();
  }
}
